import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Copy, Loader2, CheckCircle2, XCircle, MinusCircle, PowerOff } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { none: 'No account yet', pending: 'Invited, not set up yet', active: 'Active' };

const DeliveryLine = ({ label, result, testId }) => {
  const s = result?.status;
  const Icon = s === 'sent' ? CheckCircle2 : s === 'failed' ? XCircle : MinusCircle;
  const color = s === 'sent' ? 'text-green-600' : s === 'failed' ? 'text-red-600' : 'text-gray-400';
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testId}>
      <Icon className={`h-4 w-4 mt-0.5 ${color}`} />
      <span><b>{label}:</b> {s === 'sent' ? `Sent${result.is_mock ? ' (mock email, not delivered)' : ''}` : s === 'failed' ? `Failed${result.reason ? ` – ${result.reason}` : ''}` : `Skipped – ${result?.reason || 'not available'}`}</span>
    </div>
  );
};

export const HelpConnectAccessButton = ({ participantId, onSent }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  useEffect(() => {
    if (!open || !participantId) return;
    setResult(null);
    setConfirmOff(false);
    axios.get(`${API}/participants/${participantId}/helpconnect-access`).then((r) => setStatus(r.data)).catch(() => setStatus({ status: 'none' }));
  }, [open, participantId]);

  const create = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/participants/${participantId}/helpconnect-access`);
      setResult(data);
      onSent?.();
      setStatus((s) => ({ ...s, status: data.account_status === 'active' ? 'active' : 'pending' }));
      try { await navigator.clipboard.writeText(data.link); toast.success('Link created and copied'); } catch { toast.success('Link created'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the HELPConnect link');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.link); toast.success('Link copied'); } catch { toast.error('Copy failed, select the link and copy it'); }
  };

  const restore = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/participants/${participantId}/helpconnect-access/restore`);
      setStatus(data);
      onSent?.();
      toast.success('HELPConnect access restored');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore HELPConnect access');
    } finally {
      setLoading(false);
    }
  };

  const turnOff = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/participants/${participantId}/helpconnect-access/turn-off`);
      setStatus(data);
      setConfirmOff(false);
      onSent?.();
      toast.success('HELPConnect access turned off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not turn off HELPConnect access');
    } finally {
      setLoading(false);
    }
  };

  const isActive = status?.status === 'active';
  const deactivated = status?.access?.state === 'deactivated';
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={!participantId} data-testid="helpconnect-access-btn">
        <KeyRound className="h-4 w-4 mr-2" /> HELPConnect Access
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="helpconnect-access-dialog">
          <DialogHeader>
            <DialogTitle>HELPConnect Access</DialogTitle>
            <DialogDescription>
              {isActive
                ? 'This participant already has an account. A single-use password reset link (24 hours) will be created.'
                : 'A single-use account setup link (7 days) will be created for this participant.'}
              {' '}It is copied for you and also sent by text and email.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm" data-testid="helpconnect-account-status">
            Account status: <b>{status ? STATUS_LABEL[status.status] || status.status : 'Loading…'}</b>
            {status?.email && <span className="text-muted-foreground"> · {status.email}</span>}
          </div>
          {deactivated && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm flex items-center justify-between gap-3" data-testid="helpconnect-access-deactivated-box">
              <span>{status.access.reason === 'Turned off by staff' ? 'Access was turned off by staff.' : 'Access deactivated after the case closed.'} Progress and certificates are saved.</span>
              <Button size="sm" onClick={restore} disabled={loading} data-testid="helpconnect-restore-access-btn">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Restore access
              </Button>
            </div>
          )}
          {status && status.status !== 'none' && !deactivated && (
            confirmOff ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm space-y-2" data-testid="helpconnect-turn-off-confirm">
                <p>Turn off HELPConnect access now? The participant won't be able to sign in and won't get a message. Their progress and certificates are saved.</p>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setConfirmOff(false)} data-testid="helpconnect-turn-off-cancel-btn">Cancel</Button>
                  <Button size="sm" variant="destructive" onClick={turnOff} disabled={loading} data-testid="helpconnect-turn-off-confirm-btn">
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Turn off access
                  </Button>
                </div>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 justify-start px-0 w-fit" onClick={() => setConfirmOff(true)} data-testid="helpconnect-turn-off-btn">
                <PowerOff className="h-4 w-4 mr-2" />Turn off access
              </Button>
            )
          )}
          {status?.access?.state === 'ending' && (
            <p className="text-sm text-muted-foreground" data-testid="helpconnect-access-ends">
              Access ends {new Date(status.access.ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
          {result && (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex gap-2">
                <input readOnly value={result.link} className="flex-1 text-xs border rounded px-2 py-1.5 bg-muted" data-testid="helpconnect-link-input" onFocus={(e) => e.target.select()} />
                <Button size="sm" variant="outline" onClick={copy} data-testid="helpconnect-copy-link-btn"><Copy className="h-4 w-4" /></Button>
              </div>
              <DeliveryLine label="Text" result={result.delivery?.sms} testId="helpconnect-sms-result" />
              <DeliveryLine label="Email" result={result.delivery?.email} testId="helpconnect-email-result" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={create} disabled={loading || !status} data-testid="helpconnect-create-link-btn">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {result ? 'Create New Link' : isActive ? 'Create Reset Link' : 'Create Setup Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
